import React from "react";
import { useTranslation } from "react-i18next";
import { Section, Overline, SectionTitle, GoldDivider } from "@/components/Primitives";

const CHEF_IMG = "https://images.pexels.com/photos/8112980/pexels-photo-8112980.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const AMBIANCE = "https://images.pexels.com/photos/880424/pexels-photo-880424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Restaurant() {
  const { t } = useTranslation();
  const values = t("restaurant_page.values", { returnObjects: true }) || [];
  const spaces = t("restaurant_page.spaces", { returnObjects: true }) || [];

  return (
    <div data-testid="restaurant-page" className="pt-32">
      <Section testId="restaurant-hero">
        <Overline>{t("restaurant_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-8">{t("restaurant_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/75 leading-relaxed text-lg">{t("restaurant_page.intro")}</p>
      </Section>

      <Section testId="restaurant-values" className="bg-[#0A0A0A]">
        <Overline>—</Overline>
        <SectionTitle>{t("restaurant_page.values_title")}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {values.map((v, i) => (
            <div key={i} className="luxe-card p-8" data-testid={`value-${i}`}>
              <div className="text-[#D4AF37] font-serif-luxe text-3xl mb-3">{String(i+1).padStart(2,"0")}</div>
              <h3 className="font-serif-luxe text-2xl text-[#F8F5EF] mb-3">{v.title}</h3>
              <p className="text-sm text-[#F8F5EF]/70 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section testId="restaurant-chef">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden">
              <img src={CHEF_IMG} alt={t("restaurant_page.chef_title")} className="w-full h-[560px] object-cover" />
              <div className="absolute inset-0 border border-[#D4AF37]/20" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <Overline>—</Overline>
            <SectionTitle>{t("restaurant_page.chef_title")}</SectionTitle>
            <div className="text-[#F8F5EF]/75 leading-relaxed space-y-4 mb-6">
              {t("restaurant_page.chef_text").split(/\n\n+/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {t("restaurant_page.chef_highlight") && (
              <p className="font-serif-luxe text-xl text-[#D4AF37] leading-relaxed mb-8 italic" data-testid="chef-highlight">
                {t("restaurant_page.chef_highlight")}
              </p>
            )}
            <GoldDivider />
            <h3 className="font-serif-luxe text-3xl text-[#D4AF37] mb-3">{t("restaurant_page.team_title")}</h3>
            <p className="text-[#F8F5EF]/75 leading-relaxed">{t("restaurant_page.team_text")}</p>
          </div>
        </div>
      </Section>

      <Section testId="restaurant-spaces" className="bg-[#0A0A0A]">
        <Overline>—</Overline>
        <SectionTitle>{t("restaurant_page.spaces_title")}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {spaces.map((sp, i) => (
            <div key={i} className="luxe-card overflow-hidden" data-testid={`space-${i}`}>
              <img src={AMBIANCE} alt={sp.title} className="w-full h-56 object-cover" />
              <div className="p-6">
                <h3 className="font-serif-luxe text-2xl text-[#F8F5EF] mb-2">{sp.title}</h3>
                <p className="text-sm text-[#F8F5EF]/70 leading-relaxed">{sp.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
